import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  supabase,
  triggerLogin,
  isAuthorizedAdminUser,
  getAdminUser,
} from "./supabaseClient";


const ADMIN_ROUTE = process.env.REACT_APP_ADMIN_ROUTE || "/notifications";
const THEME_STORAGE_KEY = "portfolio_sidebar_theme";

const THEMES = {
  light: {
    label: "Morning",
    bg: "#ffffff",
    text: "#121212",
    muted: "#696969",
    panel: "#f3f3f3",
    panelText: "#121212",
    panelMuted: "#777777",
    panelBorder: "#e5e5e5",
    divider: "rgba(18,18,18,0.12)",
    accent: "#121212",
    active: "#000000",
    activeTint: "rgba(18,18,18,0.075)",
    status: "#5aa46d",
    statusShadow: "rgba(90,164,109,0.35)",
    shadow: "rgba(0,0,0,0.08)",
    toggleBg: "#ffffff",
    toggleText: "#121212",
    pageBg: "#ffffff",
    pageText: "#121212",
    pageMuted: "#696969",
    pageSurface: "#00000029",
    pageBorder: "rgba(18,18,18,0.12)",
    shimmerA: "#ececec",
    shimmerB: "#f7f7f7",
    tagBg: "#0000001f",
    cardBorder: "rgba(0,0,0,0.15)",
  },
  warm: {
    label: "Evening",
    bg: "#f4eadc",
    text: "#2f2f2f",
    muted: "#6f655b",
    panel: "#e8dccb",
    panelText: "#2f2f2f",
    panelMuted: "#74695f",
    panelBorder: "rgba(80,65,48,0.12)",
    divider: "rgba(47,47,47,0.13)",
    accent: "#8c5a32",
    active: "#5a351c",
    activeTint: "rgba(140,90,50,0.14)",
    status: "#7c9461",
    statusShadow: "rgba(124,148,97,0.35)",
    shadow: "rgba(77,52,31,0.11)",
    toggleBg: "#f4eadc",
    toggleText: "#2f2f2f",
    pageBg: "#f4eadc",
    pageText: "#2f2f2f",
    pageMuted: "#6f655b",
    pageSurface: "#ff95003b",
    pageBorder: "rgba(47,47,47,0.13)",
    shimmerA: "#e3d7c7",
    shimmerB: "#f0e5d7",
    tagBg: "#ff98004a",
    cardBorder: "#f4433647",
  },
  dark: {
    label: "Night",
    bg: "#121212",
    text: "#ffffff",
    muted: "#b8b8b8",
    panel: "#1f1f1f",
    panelText: "#ffffff",
    panelMuted: "#a6a6a6",
    panelBorder: "rgba(255,255,255,0.1)",
    divider: "rgba(255,255,255,0.13)",
    accent: "#ffffff",
    active: "#ffffff",
    activeTint: "rgba(255,255,255,0.11)",
    status: "#7bd88f",
    statusShadow: "rgba(123,216,143,0.35)",
    shadow: "rgba(0,0,0,0.35)",
    toggleBg: "#121212",
    toggleText: "#ffffff",
    pageBg: "#121212",
    pageText: "#ffffff",
    pageMuted: "#b8b8b8",
    pageSurface: "#ffffff0d",
    pageBorder: "rgba(255,255,255,0.13)",
    shimmerA: "#222222",
    shimmerB: "#333333",
    tagBg: "#eeeeee63",
    cardBorder: "rgba(255,255,255,0.45)",
  },
};

const THEME_KEYS = ["light", "warm", "dark"];

const styles = `
  * { box-sizing: border-box; }

  .sidebar-theme-host {
    width: 340px;
    min-width: 340px;
    color: var(--sidebar-text);
  }

  .sidebar {
    width: 340px;
    min-height: 100vh;
    height: max-content;
    background: var(--sidebar-bg);
    color: var(--sidebar-text);
    padding: clamp(22px, 4vh, 38px) 24px;
    display: flex;
    flex-direction: column;
    font-family: 'Outfit', sans-serif;
    border-right: 1px solid var(--sidebar-divider);
    position: sticky;
    top: 0;
    z-index: 70;
    overflow: hidden;
    transition: background 240ms ease, color 240ms ease, border-color 240ms ease;
  }

  .sidebar-mobile-toggle { display: none; }

  .sidebar-top {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  .sidebar-name {
    font-family: "Audemars";
    font-size: clamp(34px, 5.1vh, 46px);
    font-weight: 500;
    color: var(--sidebar-text);
    margin: 0 0 8px;
    letter-spacing: 0px;
    line-height: 1.05;
    cursor: default;
    user-select: none;
  }

  .sidebar-name:active { transform: scale(0.99); }

  .sidebar-credentials {
    font-size: 11px;
    color: var(--sidebar-muted);
    font-family: 'JetBrains Mono', 'Courier New', monospace;
    letter-spacing: 1px;
    margin: 0;
    line-height: 1.6;
    font-weight: 500;
  }

  .sidebar-bio {
    margin: clamp(14px, 2.5vh, 24px) 0 clamp(14px, 2.8vh, 28px);
  }

  .bio-paragraph {
    font-size: clamp(12px, 1.65vh, 13px);
    color: var(--sidebar-muted);
    line-height: 1.58;
    margin: 0;
  }

  .theme-switch {
    position: relative;
    width: 100%;
    height: 42px;
    padding: 4px;
    border: 1px solid var(--sidebar-panel-border);
    border-radius: 999px;
    background: var(--sidebar-panel);
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    margin-bottom: 10px;
  }

  .theme-switch__thumb {
    position: absolute;
    top: 4px;
    left: 4px;
    width: calc((100% - 8px) / 3);
    height: calc(100% - 8px);
    border-radius: 999px;
    background: var(--sidebar-accent);
    transform: translateX(calc(var(--theme-index) * 100%));
    transition: transform 240ms cubic-bezier(0.22, 1, 0.36, 1), background 240ms ease;
    z-index: 0;
  }

  .theme-switch button {
    position: relative;
    z-index: 1;
    border: 0;
    background: transparent;
    color: var(--sidebar-muted);
    display: grid;
    place-items: center;
    cursor: pointer;
    border-radius: 999px;
  }

  .theme-switch button.is-active {
    color: var(--sidebar-bg);
  }

  .theme-switch svg {
    width: 16px;
    height: 16px;
  }

  .explore-box {
    background: var(--sidebar-panel);
    border-radius: 18px;
    padding: clamp(16px, 2.8vh, 22px);
    margin-bottom: clamp(16px, 3.6vh, 34px);
    border: 1px solid var(--sidebar-panel-border);
    box-shadow: 0 14px 34px var(--sidebar-shadow);
    flex-shrink: 0;
  }

  .explore-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: clamp(12px, 2.3vh, 18px);
  }

  .explore-title,
  .find-me-title {
    font-size: 10px;
    font-family: 'JetBrains Mono', 'Courier New', monospace;
    font-weight: 700;
    color: var(--sidebar-panel-text);
    letter-spacing: 1.5px;
    text-transform: uppercase;
  }

  .menu-items {
    display: flex;
    flex-direction: column;
    gap: clamp(8px, 1.65vh, 14px);
  }

  .menu-item-group {
    display: flex;
    flex-direction: column;
    gap: clamp(6px, 1.2vh, 10px);
  }

  .menu-item,
  .submenu-item {
    border-radius: 12px;
    cursor: pointer;
    transition: transform 180ms ease, background 180ms ease, padding 180ms ease;
  }

  .menu-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: clamp(6px, 1.05vh, 8px) 0;
  }

  .menu-item.is-active {
    background: var(--sidebar-active-tint);
    padding-left: 10px;
    padding-right: 10px;
  }

  .menu-item:hover {
    transform: translateX(4px);
  }

  .menu-item-left {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .menu-number {
    font-size: 10px;
    font-family: 'JetBrains Mono', 'Courier New', monospace;
    font-weight: 700;
    color: var(--sidebar-panel-muted);
    letter-spacing: 0.5px;
    width: 20px;
    flex: 0 0 auto;
  }

  .menu-label {
    color: var(--sidebar-panel-text);
    font-size: 14px;
    font-weight: 500;
  }

  .menu-arrow {
    color: var(--sidebar-panel-muted);
    font-size: 14px;
    opacity: 0.7;
    transition: transform 180ms ease, color 180ms ease, opacity 180ms ease;
  }

  .menu-item:hover .menu-label,
  .menu-item:hover .menu-arrow,
  .menu-item.is-active .menu-label,
  .menu-item.is-active .menu-arrow,
  .menu-item.is-active .menu-number {
    color: var(--sidebar-active);
    opacity: 1;
  }

  .menu-arrow.expanded {
    color: var(--sidebar-active);
    opacity: 1;
    transform: rotate(90deg);
  }

  .submenu-items {
    display: flex;
    flex-direction: column;
    gap: clamp(5px, 0.95vh, 8px);
    padding-left: 18px;
    border-left: 2px solid var(--sidebar-divider);
    margin-top: 2px;
    margin-left: 10px;
  }

  .submenu-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: clamp(5px, 0.8vh, 7px) 8px;
  }

  .submenu-item.is-active {
    background: var(--sidebar-active-tint);
  }

  .submenu-item:hover {
    transform: translateX(3px);
  }

  .submenu-label {
    color: var(--sidebar-panel-muted);
    font-size: 12px;
    font-weight: 500;
  }

  .submenu-arrow {
    color: var(--sidebar-panel-muted);
    font-size: 11px;
    opacity: 0.65;
  }

  .submenu-item:hover .submenu-label,
  .submenu-item:hover .submenu-arrow,
  .submenu-item.is-active .submenu-label,
  .submenu-item.is-active .submenu-arrow {
    color: var(--sidebar-active);
    opacity: 1;
  }

  .admin-menu-item .menu-number,
  .admin-menu-item .menu-label,
  .admin-menu-item .menu-arrow {
    color: var(--sidebar-accent);
  }

  .find-me-section {
    flex: 1 1 auto;
    min-height: 42px;
  }

  .find-me-title {
    margin: 0 0 8px;
  }

  .find-me-text {
    font-size: 13px;
    color: var(--sidebar-muted);
    margin: 0;
    font-weight: 500;
    line-height: 1.45;
  }

  .sidebar-footer {
    padding-top: clamp(14px, 2.8vh, 26px);
    border-top: 1px solid var(--sidebar-divider);
    flex-shrink: 0;
  }

  .footer-time {
    font-size: 11px;
    font-family: 'JetBrains Mono', 'Courier New', monospace;
    font-weight: 700;
    color: var(--sidebar-text);
    letter-spacing: 0.8px;
    margin: 0 0 8px;
  }

  .footer-status {
    font-size: 11px;
    font-family: 'JetBrains Mono', 'Courier New', monospace;
    color: var(--sidebar-muted);
  }

  .status-line {
    margin: 4px 0;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
    letter-spacing: 0.5px;
  }

  .status-dot {
    width: 8px;
    height: 8px;
    background-color: var(--sidebar-status);
    border-radius: 50%;
    display: inline-block;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0%, 100% {
      box-shadow: 0 0 0 0 var(--sidebar-status-shadow);
    }
    50% {
      box-shadow: 0 0 0 6px transparent;
    }
  }

  @media (max-height: 720px) {
    .sidebar {
      padding-top: 18px;
      padding-bottom: 18px;
    }

    .sidebar-name {
      font-size: 34px;
    }

    .sidebar-bio {
      display: none;
    }

    .explore-box {
      margin-bottom: 14px;
    }

    .find-me-section {
      min-height: 34px;
    }
  }

  @media (max-height: 620px) {
    .find-me-section {
      display: none;
    }

    .sidebar-footer {
      padding-top: 12px;
    }
  }

  @media (max-width: 768px) {
    .sidebar-theme-host {
      width: 0;
      min-width: 0;
    }

    .sidebar {
      position: fixed;
      top: 0;
      left: 0;
      width: min(340px, calc(100vw - 42px));
      height: 100dvh;
      padding: 24px 20px;
      transform: translateX(0);
      transition: transform 280ms cubic-bezier(0.23, 1, 0.32, 1), background 240ms ease;
      box-shadow:
        24px 0 60px rgba(0, 0, 0, 0.24),
        inset -1px 0 0 var(--sidebar-divider);
    }

    .sidebar.is-mobile-hidden {
      transform: translateX(-100%);
    }

    .sidebar-mobile-toggle {
      display: grid;
      position: fixed;
      left: 0;
      top: 50%;
      width: 34px;
      height: 66px;
      border: 1px solid var(--sidebar-divider);
      border-left: 0;
      border-radius: 0 999px 999px 0;
      background: var(--sidebar-toggle-bg);
      color: var(--sidebar-toggle-text);
      place-items: center;
      cursor: pointer;
      box-shadow: 12px 0 28px rgba(0, 0, 0, 0.16);
      transform: translateY(-50%);
      z-index: 99999;
      font-size: 22px;
      font-weight: 700;
      line-height: 1;
    }

    .sidebar-name {
      font-size: clamp(34px, 6vh, 40px);
    }

    .explore-box {
      padding: 20px;
    }
  }

  @media (min-width: 769px) {
    .sidebar {
      transform: none !important;
    }

    .sidebar-mobile-toggle {
      display: none !important;
    }
  }
`;

const getIsMobile = () =>
  typeof window !== "undefined" && window.innerWidth <= 768;

const getInitialTheme = () => {
  if (typeof window === "undefined") return "light";
  return localStorage.getItem(THEME_STORAGE_KEY) || "light";
};

const createThemeVars = (themeKey) => {
  const theme = THEMES[themeKey] || THEMES.light;

  return {
    "--theme-index": THEME_KEYS.indexOf(themeKey),
    "--sidebar-bg": theme.bg,
    "--sidebar-text": theme.text,
    "--sidebar-muted": theme.muted,
    "--sidebar-panel": theme.panel,
    "--sidebar-panel-text": theme.panelText,
    "--sidebar-panel-muted": theme.panelMuted,
    "--sidebar-panel-border": theme.panelBorder,
    "--sidebar-divider": theme.divider,
    "--sidebar-accent": theme.accent,
    "--sidebar-active": theme.active,
    "--sidebar-active-tint": theme.activeTint,
    "--sidebar-status": theme.status,
    "--sidebar-status-shadow": theme.statusShadow,
    "--sidebar-shadow": theme.shadow,
    "--sidebar-toggle-bg": theme.toggleBg,
    "--sidebar-toggle-text": theme.toggleText,
  };
};

const applyPortfolioTheme = (themeKey) => {
  if (typeof document === "undefined") return;

  const theme = THEMES[themeKey] || THEMES.light;
  const root = document.documentElement;

  root.dataset.portfolioTheme = themeKey;
  root.style.setProperty("--portfolio-page-bg", theme.pageBg);
  root.style.setProperty("--portfolio-page-text", theme.pageText);
  root.style.setProperty("--portfolio-page-muted", theme.pageMuted);
  root.style.setProperty("--portfolio-surface", theme.pageSurface);
  root.style.setProperty("--portfolio-border", theme.pageBorder);
  root.style.setProperty("--portfolio-accent", theme.accent);
  root.style.setProperty("--portfolio-active-tint", theme.activeTint);
  root.style.setProperty("--portfolio-shimmer-a", theme.shimmerA);
  root.style.setProperty("--portfolio-shimmer-b", theme.shimmerB);
  root.style.setProperty("--portfolio-tag-bg", theme.tagBg);
  root.style.setProperty("--portfolio-cardBorder", theme.cardBorder);

  document.body.style.backgroundColor = theme.pageBg;
  document.body.style.color = theme.pageText;

  window.dispatchEvent(
    new CustomEvent("portfolio-theme-change", {
      detail: { theme: themeKey, tokens: theme },
    })
  );
};

function MorningIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2" />
      <path d="M12 2v2M12 20v2M4 12H2M22 12h-2M5 5l1.5 1.5M17.5 17.5 19 19M19 5l-1.5 1.5M6.5 17.5 5 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function EveningIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M7 14a5 5 0 0 1 10 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M12 4v3M5.8 7.8 8 10M18.2 7.8 16 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function NightIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M20 15.4A7.8 7.8 0 0 1 8.6 4 8.3 8.3 0 1 0 20 15.4Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

export default function Sidebar() {
  const [time, setTime] = useState("12:35:55 PM");
  const [expandedMenu, setExpandedMenu] = useState("01");
  const [isMobile, setIsMobile] = useState(getIsMobile);
  const [mobileHidden, setMobileHidden] = useState(getIsMobile);
const [isAdmin, setIsAdmin] = useState(false);
const [authReady, setAuthReady] = useState(false);

  const [themeKey, setThemeKey] = useState(getInitialTheme);

const clickTimeout = useRef(null);
const clickCount = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();

  const themeVars = useMemo(() => createThemeVars(themeKey), [themeKey]);

  const currentPath = location.pathname.replace(/\/+$/, "") || "/";

  const routeMatches = (paths) =>
    paths.some((path) => {
      const target = path.replace(/\/+$/, "") || "/";
      return currentPath === target || currentPath.startsWith(`${target}/`);
    });

  const workActive = routeMatches([
    "/featured-projects",
    "/case-study",
    "/blogs",
    "/all-projects",
    "/project",
  ]);

  const classFor = (base, active) => `${base}${active ? " is-active" : ""}`;

  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, themeKey);
    applyPortfolioTheme(themeKey);
  }, [themeKey]);

  useEffect(() => {
    applyPortfolioTheme(themeKey);
  }, []);

  useEffect(() => {
    if (workActive) setExpandedMenu("01");
  }, [workActive]);

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      let hours = now.getHours();
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");
      const period = hours >= 12 ? "PM" : "AM";

      hours = hours % 12;
      hours = hours === 0 ? 12 : hours;

      setTime(`${String(hours).padStart(2, "0")}:${minutes}:${seconds} ${period}`);
    };

    updateTime();
    const interval = window.setInterval(updateTime, 1000);

    return () => window.clearInterval(interval);
  }, []);

useEffect(() => {
  let subscription;

  const initAdminAuth = async () => {
    if (!supabase) {
      setAuthReady(true);
      setIsAdmin(false);
      return;
    }

    try {
      const admin = await getAdminUser();
      setIsAdmin(Boolean(admin));
    } catch (err) {
      console.error("Sidebar auth init failed:", err);
      setIsAdmin(false);
    } finally {
      setAuthReady(true);
    }

    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAdmin(isAuthorizedAdminUser(session?.user));
      setAuthReady(true);
    });

    subscription = data.subscription;
  };

  initAdminAuth();

  return () => {
    subscription?.unsubscribe();
  };
}, []);




  useEffect(() => {
    const checkWidth = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);

      if (!mobile) setMobileHidden(false);
    };

    checkWidth();
    window.addEventListener("resize", checkWidth);

    return () => window.removeEventListener("resize", checkWidth);
  }, []);

  useEffect(() => {
    return () => {
      if (clickTimeout.current) window.clearTimeout(clickTimeout.current);
    };
  }, []);

const handleSecretClick = () => {
  if (isAdmin || !supabase) return;

  clickCount.current += 1;

  if (clickTimeout.current) window.clearTimeout(clickTimeout.current);

  clickTimeout.current = window.setTimeout(() => {
    clickCount.current = 0;
  }, 1500);

  if (clickCount.current >= 5) {
    clickCount.current = 0;
    triggerLogin();
  }
};


  const toggleMenu = (menuId) => {
    setExpandedMenu(expandedMenu === menuId ? null : menuId);
  };

  const goTo = (path) => {
    navigate(path);
    if (isMobile) setMobileHidden(true);
  };

  return (
    <>
      <style>{styles}</style>

      <div className="sidebar-theme-host" style={themeVars}>
        {isMobile && (
          <button
            className="sidebar-mobile-toggle"
            type="button"
            onClick={() => setMobileHidden((hidden) => !hidden)}
            aria-label={mobileHidden ? "Open sidebar" : "Close sidebar"}
          >
            <span>{mobileHidden ? "›" : "‹"}</span>
          </button>
        )}

        <aside className={`sidebar ${isMobile && mobileHidden ? "is-mobile-hidden" : ""}`}>
          <div className="sidebar-top">
            <div>
              <h1 className="sidebar-name" onClick={handleSecretClick}>
                Ragav Krishna
              </h1>

              <p className="sidebar-credentials">UI/UX Designer & Developer</p>
            </div>

            <div className="theme-switch" aria-label="Theme selector">
              <span className="theme-switch__thumb" />

              <button
                type="button"
                className={themeKey === "light" ? "is-active" : ""}
                onClick={() => setThemeKey("light")}
                aria-label="Morning theme"
                title="Morning"
              >
                <MorningIcon />
              </button>

              <button
                type="button"
                className={themeKey === "warm" ? "is-active" : ""}
                onClick={() => setThemeKey("warm")}
                aria-label="Evening theme"
                title="Evening"
              >
                <EveningIcon />
              </button>

              <button
                type="button"
                className={themeKey === "dark" ? "is-active" : ""}
                onClick={() => setThemeKey("dark")}
                aria-label="Night theme"
                title="Night"
              >
                <NightIcon />
              </button>
            </div>
          </div>

          <div className="sidebar-bio">
            <p className="bio-paragraph">
              I am a designer who loves building delightful software that solves real problems.
            </p>
          </div>

          <div className="explore-box" data-platform>
            <div className="explore-header">
              <span className="explore-title">My Pages</span>
            </div>

            <div className="menu-items">
              <div className="menu-item-group">
                <div className={classFor("menu-item", workActive)} onClick={() => toggleMenu("01")}>
                  <div className="menu-item-left">
                    <span className="menu-number">01.</span>
                    <span className="menu-label">Work</span>
                  </div>
                  <span className={`menu-arrow ${expandedMenu === "01" ? "expanded" : ""}`}>
                    →
                  </span>
                </div>

                {expandedMenu === "01" && (
                  <div className="submenu-items">
                    <div
                      className={classFor("submenu-item", routeMatches(["/featured-projects"]))}
                      onClick={() => goTo("/featured-projects")}
                    >
                      <span className="submenu-label">Featured Projects</span>
                      <span className="submenu-arrow">→</span>
                    </div>

                    <div
                      className={classFor("submenu-item", routeMatches(["/case-study"]))}
                      onClick={() => goTo("/case-study")}
                    >
                      <span className="submenu-label">Case Study</span>
                      <span className="submenu-arrow">→</span>
                    </div>

                    <div
                      className={classFor("submenu-item", routeMatches(["/blogs"]))}
                      onClick={() => goTo("/blogs")}
                    >
                      <span className="submenu-label">Blogs</span>
                      <span className="submenu-arrow">→</span>
                    </div>

                    <div
                      className={classFor("submenu-item", routeMatches(["/all-projects"]))}
                      onClick={() => goTo("/all-projects")}
                    >
                      <span className="submenu-label">All Projects</span>
                      <span className="submenu-arrow">→</span>
                    </div>
                  </div>
                )}
              </div>

              <div
                className={classFor("menu-item", routeMatches(["/about-me"]))}
                onClick={() => goTo("/about-me")}
              >
                <div className="menu-item-left">
                  <span className="menu-number">02.</span>
                  <span className="menu-label">About Me</span>
                </div>
                <span className="menu-arrow">→</span>
              </div>

              <div
                className={classFor("menu-item", routeMatches(["/cards-showcase"]))}
                onClick={() => goTo("/cards-showcase")}
              >
                <div className="menu-item-left">
                  <span className="menu-number">03.</span>
                  <span className="menu-label">Character Cards</span>
                </div>
                <span className="menu-arrow">→</span>
              </div>

              {isAdmin && (
                <div
                  className={classFor("menu-item admin-menu-item", routeMatches([ADMIN_ROUTE]))}
                  onClick={() => goTo(ADMIN_ROUTE)}
                >
                  <div className="menu-item-left">
                    <span className="menu-number">04.</span>
                    <span className="menu-label">Notifications</span>
                  </div>
                  <span className="menu-arrow">→</span>
                </div>
              )}
            </div>
          </div>

          <div className="sidebar-footer">
            <p className="footer-time">{time}</p>
            <div className="footer-status">
              <p className="status-line">
                <span className="status-dot"></span>
                Chennai, IND
              </p>
            </div>
          </div>

          <p
            style={{
              fontSize: "11px",
              color: "var(--sidebar-panel-text)",
            }}
          >
            In a hurry?{" "}
            <a
              href="#/resume"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: "12px",
                textDecoration: "underline",
                color: "var(--sidebar-panel-text)",
                cursor: "pointer",
              }}
            >
              View My Resume here
            </a>
          </p>
        </aside>
      </div>
    </>
  );
}








